import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchAdminOverview } from '../../lib/adminApi';
import { useAdminAuth } from '../../context/AdminAuthContext';

interface OverviewResponse {
  contacts: {
    total: number;
    new: number;
    inProgress: number;
    closed: number;
  };
  jobs: {
    total: number;
    new: number;
    reviewing: number;
    contacted: number;
    rejected: number;
  };
}

const AdminDashboardPage: React.FC = () => {
  const { token, user, logout } = useAdminAuth();
  const [overview, setOverview] = useState<OverviewResponse | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) return;
    fetchAdminOverview(token)
      .then(setOverview)
      .catch(err => setError(err.message));
  }, [token]);

  return (
    <div className="min-h-screen bg-slate-950 text-white px-4 py-8">
      <div className="max-w-6xl mx-auto">
        <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-10">
          <div>
            <p className="text-sm text-slate-400 uppercase tracking-wide">Welcome back</p>
            <h1 className="text-3xl font-bold">{user?.name}</h1>
          </div>
          <div className="flex gap-3">
            <Link
              to="/admin/manage"
              className="px-4 py-2 rounded-full border border-white/20 hover:border-white transition"
            >
              Go to Management
            </Link>
            <button
              onClick={logout}
              className="px-4 py-2 rounded-full bg-red-500/80 hover:bg-red-500 transition"
            >
              Logout
            </button>
          </div>
        </header>

        {error && <p className="text-red-400 mb-6">{error}</p>}

        <div className="grid md:grid-cols-2 gap-6">
          <section className="rounded-3xl border border-white/10 p-6 bg-white/[0.02]">
            <h2 className="text-xl font-semibold mb-4">Contact Messages</h2>
            <div className="grid grid-cols-2 gap-4">
              <Stat label="Total" value={overview?.contacts.total ?? 0} />
              <Stat label="New" value={overview?.contacts.new ?? 0} />
              <Stat label="In Progress" value={overview?.contacts.inProgress ?? 0} />
              <Stat label="Closed" value={overview?.contacts.closed ?? 0} />
            </div>
          </section>

          <section className="rounded-3xl border border-white/10 p-6 bg-white/[0.02]">
            <h2 className="text-xl font-semibold mb-4">Job Applications</h2>
            <div className="grid grid-cols-2 gap-4">
              <Stat label="Total" value={overview?.jobs.total ?? 0} />
              <Stat label="New" value={overview?.jobs.new ?? 0} />
              <Stat label="Reviewing" value={overview?.jobs.reviewing ?? 0} />
              <Stat label="Contacted" value={overview?.jobs.contacted ?? 0} />
              <Stat label="Rejected" value={overview?.jobs.rejected ?? 0} />
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

const Stat: React.FC<{ label: string; value: number }> = ({ label, value }) => (
  <div className="rounded-2xl bg-slate-900/60 border border-white/5 p-4">
    <p className="text-sm text-slate-400">{label}</p>
    <p className="text-3xl font-bold">{value}</p>
  </div>
);

export default AdminDashboardPage;

